import type {
  GameStateResponse,
  UpgradeBuildingRequest,
  StartResearchRequest,
  SaveBlueprintRequest,
  BuildShipsRequest,
  SaveFleetFormationRequest,
  AttackNpcRequest,
  CombatReport,
  FleetSlot,
  HullClass,
  ReferralStatusResponse,
  ReferralClaimResponse,
} from '@galaxy/shared';

const API_BASE = import.meta.env.VITE_GALAXY_API_URL || import.meta.env.VITE_API_URL || '';

class ApiClient {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('galaxy_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('galaxy_token', token);
  }

  getToken(): string | null {
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('galaxy_token');
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || `HTTP error ${response.status}`);
    }

    return data as T;
  }

  async loginDemo(username?: string): Promise<{ token: string; state: GameStateResponse }> {
    const res = await this.request<{ token: string; state: GameStateResponse }>('/api/auth/demo', {
      method: 'POST',
      body: JSON.stringify({ username }),
    });
    this.setToken(res.token);
    return res;
  }

  async loginTelegram(initData: string, startParam?: string): Promise<{ token: string; state: GameStateResponse }> {
    const res = await this.request<{ token: string; state: GameStateResponse }>('/api/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData, startParam }),
    });
    this.setToken(res.token);
    return res;
  }

  async getState(): Promise<GameStateResponse> {
    return this.request<GameStateResponse>('/api/game/state');
  }

  async upgradeBuilding(buildingType: string): Promise<{ success: boolean; state: GameStateResponse }> {
    return this.request<{ success: boolean; state: GameStateResponse }>('/api/game/buildings/upgrade', {
      method: 'POST',
      body: JSON.stringify({ building_type: buildingType } as UpgradeBuildingRequest),
    });
  }

  async startResearch(techId: string): Promise<{ success: boolean; state: GameStateResponse }> {
    return this.request<{ success: boolean; state: GameStateResponse }>('/api/game/research/start', {
      method: 'POST',
      body: JSON.stringify({ tech_id: techId } as StartResearchRequest),
    });
  }

  async saveBlueprint(params: {
    name: string;
    hull_id: HullClass;
    modules: string[];
  }): Promise<{ success: boolean; state: GameStateResponse }> {
    return this.request<{ success: boolean; state: GameStateResponse }>('/api/game/ships/blueprints', {
      method: 'POST',
      body: JSON.stringify(params as SaveBlueprintRequest),
    });
  }

  async produceShips(designId: string, count: number): Promise<{ success: boolean; state: GameStateResponse }> {
    return this.request<{ success: boolean; state: GameStateResponse }>('/api/game/ships/produce', {
      method: 'POST',
      body: JSON.stringify({ design_id: designId, count } as BuildShipsRequest),
    });
  }

  async saveFleetFormation(slots: FleetSlot[]): Promise<{ success: boolean; state: GameStateResponse }> {
    return this.request<{ success: boolean; state: GameStateResponse }>('/api/game/fleet/formation', {
      method: 'POST',
      body: JSON.stringify({ slots } as SaveFleetFormationRequest),
    });
  }

  async attackNpc(npcId: string): Promise<{ success: boolean; report: CombatReport; state: GameStateResponse }> {
    return this.request<{ success: boolean; report: CombatReport; state: GameStateResponse }>(
      '/api/game/combat/attack-npc',
      {
        method: 'POST',
        body: JSON.stringify({ npc_id: npcId } as AttackNpcRequest),
      }
    );
  }

  async getNpcList(): Promise<{ npcs: any[] }> {
    return this.request<{ npcs: any[] }>('/api/game/npcs');
  }

  async getGalaxyMap(params?: {
    galaxy_id?: string;
    sector_id?: number;
    min_x?: number;
    max_x?: number;
    min_y?: number;
    max_y?: number;
  }): Promise<import('@galaxy/shared').GalaxyMapResponse> {
    const sp = new URLSearchParams();
    if (params?.galaxy_id) sp.set('galaxy_id', params.galaxy_id);
    if (params?.sector_id) sp.set('sector_id', String(params.sector_id));
    if (params?.min_x !== undefined) sp.set('min_x', String(params.min_x));
    if (params?.max_x !== undefined) sp.set('max_x', String(params.max_x));
    if (params?.min_y !== undefined) sp.set('min_y', String(params.min_y));
    if (params?.max_y !== undefined) sp.set('max_y', String(params.max_y));
    const qs = sp.toString() ? `?${sp.toString()}` : '';
    return this.request<import('@galaxy/shared').GalaxyMapResponse>(`/api/game/galaxy/map${qs}`);
  }

  async searchGalaxyCoordinates(
    sector_id: number,
    x: number,
    y: number,
    galaxy_id?: string
  ): Promise<{ entity: import('@galaxy/shared').GalaxyEntity | null; x: number; y: number; sector_id: number }> {
    const sp = new URLSearchParams({ sector_id: String(sector_id), x: String(x), y: String(y) });
    if (galaxy_id) sp.set('galaxy_id', galaxy_id);
    return this.request<{ entity: import('@galaxy/shared').GalaxyEntity | null; x: number; y: number; sector_id: number }>(
      `/api/game/galaxy/search?${sp.toString()}`
    );
  }

  async dispatchFleet(
    params: import('@galaxy/shared').DispatchFleetRequest
  ): Promise<{ success: boolean; movement: import('@galaxy/shared').FleetMovementItem; state: GameStateResponse }> {
    return this.request<{ success: boolean; movement: import('@galaxy/shared').FleetMovementItem; state: GameStateResponse }>(
      '/api/game/fleet/dispatch',
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
  }

  async recallFleet(
    movement_id: string
  ): Promise<{ success: boolean; movement: import('@galaxy/shared').FleetMovementItem; state: GameStateResponse }> {
    return this.request<{ success: boolean; movement: import('@galaxy/shared').FleetMovementItem; state: GameStateResponse }>(
      '/api/game/fleet/recall',
      {
        method: 'POST',
        body: JSON.stringify({ movement_id }),
      }
    );
  }

  async getFleetMovements(): Promise<{ movements: import('@galaxy/shared').FleetMovementItem[] }> {
    return this.request<{ movements: import('@galaxy/shared').FleetMovementItem[] }>('/api/game/fleet/movements');
  }

  async getScoutReports(): Promise<{ reports: any[] }> {
    return this.request<{ reports: any[] }>('/api/game/scout/reports');
  }

  async getMyAlliance(): Promise<{ alliance: import('@galaxy/shared').AllianceDetail | null }> {
    return this.request<{ alliance: import('@galaxy/shared').AllianceDetail | null }>('/api/game/alliance/my');
  }

  async getAllianceLeaderboard(): Promise<{ leaderboard: import('@galaxy/shared').AllianceSummary[] }> {
    return this.request<{ leaderboard: import('@galaxy/shared').AllianceSummary[] }>('/api/game/alliance/leaderboard');
  }

  async createAlliance(
    params: import('@galaxy/shared').CreateAllianceRequest
  ): Promise<{ success: boolean; alliance: import('@galaxy/shared').AllianceDetail; state: GameStateResponse }> {
    return this.request<{ success: boolean; alliance: import('@galaxy/shared').AllianceDetail; state: GameStateResponse }>(
      '/api/game/alliance/create',
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
  }

  async joinAlliance(
    alliance_id: string
  ): Promise<{ success: boolean; alliance: import('@galaxy/shared').AllianceDetail; state: GameStateResponse }> {
    return this.request<{ success: boolean; alliance: import('@galaxy/shared').AllianceDetail; state: GameStateResponse }>(
      '/api/game/alliance/join',
      {
        method: 'POST',
        body: JSON.stringify({ alliance_id }),
      }
    );
  }

  async leaveAlliance(): Promise<{ success: boolean; state: GameStateResponse }> {
    return this.request<{ success: boolean; state: GameStateResponse }>('/api/game/alliance/leave', {
      method: 'POST',
    });
  }

  async donateAlliance(
    credits: number
  ): Promise<{ success: boolean; donateResult: any; alliance: import('@galaxy/shared').AllianceDetail; state: GameStateResponse }> {
    return this.request<{ success: boolean; donateResult: any; alliance: import('@galaxy/shared').AllianceDetail; state: GameStateResponse }>(
      '/api/game/alliance/donate',
      {
        method: 'POST',
        body: JSON.stringify({ credits }),
      }
    );
  }

  async kickAllianceMember(
    target_player_id: string
  ): Promise<{ success: boolean; alliance: import('@galaxy/shared').AllianceDetail }> {
    return this.request<{ success: boolean; alliance: import('@galaxy/shared').AllianceDetail }>('/api/game/alliance/kick', {
      method: 'POST',
      body: JSON.stringify({ target_player_id }),
    });
  }

  async promoteAllianceMember(
    target_player_id: string,
    role: string
  ): Promise<{ success: boolean; alliance: import('@galaxy/shared').AllianceDetail }> {
    return this.request<{ success: boolean; alliance: import('@galaxy/shared').AllianceDetail }>('/api/game/alliance/promote', {
      method: 'POST',
      body: JSON.stringify({ target_player_id, role }),
    });
  }

  async disbandAlliance(): Promise<{ success: boolean; state: GameStateResponse }> {
    return this.request<{ success: boolean; state: GameStateResponse }>('/api/game/alliance/disband', {
      method: 'POST',
    });
  }

  // Daily Rewards & Boost Inventory
  async getDailyRewards(): Promise<{ success: boolean; status: import('@galaxy/shared').DailyRewardsStatus }> {
    return this.request<{ success: boolean; status: import('@galaxy/shared').DailyRewardsStatus }>('/api/boost/daily-rewards');
  }

  async claimDailyReward(): Promise<{ success: boolean; reward: import('@galaxy/shared').DailyRewardDay; streak: number; status: import('@galaxy/shared').DailyRewardsStatus }> {
    return this.request<{ success: boolean; reward: import('@galaxy/shared').DailyRewardDay; streak: number; status: import('@galaxy/shared').DailyRewardsStatus }>('/api/boost/daily-rewards/claim', {
      method: 'POST',
    });
  }

  async getBoostInventory(): Promise<{ success: boolean; items: import('@galaxy/shared').BoostInventoryItem[] }> {
    return this.request<{ success: boolean; items: import('@galaxy/shared').BoostInventoryItem[] }>('/api/boost/inventory');
  }

  async consumeBoost(inventory_id: string, target_type: 'building' | 'research' | 'shipyard' | 'active_buff', target_id: string): Promise<{ success: boolean; time_saved_ms: number; finish_at_before: number; finish_at_after: number; inventory: import('@galaxy/shared').BoostInventoryItem[] }> {
    return this.request<{ success: boolean; time_saved_ms: number; finish_at_before: number; finish_at_after: number; inventory: import('@galaxy/shared').BoostInventoryItem[] }>('/api/boost/consume', {
      method: 'POST',
      body: JSON.stringify({ inventory_id, target_type, target_id }),
    });
  }

  async getBoostUsageLogs(): Promise<{ success: boolean; logs: import('@galaxy/shared').BoostUsageLog[] }> {
    return this.request<{ success: boolean; logs: import('@galaxy/shared').BoostUsageLog[] }>('/api/boost/usage-logs');
  }

  async getQuests(): Promise<{ success: boolean; quests: import('@galaxy/shared').DailyQuest[] }> {
    return this.request<{ success: boolean; quests: import('@galaxy/shared').DailyQuest[] }>('/api/boost/quests');
  }

  async claimQuest(quest_id: string): Promise<{ success: boolean; reward_item_id: string; quests: import('@galaxy/shared').DailyQuest[]; inventory: import('@galaxy/shared').BoostInventoryItem[] }> {
    return this.request<{ success: boolean; reward_item_id: string; quests: import('@galaxy/shared').DailyQuest[]; inventory: import('@galaxy/shared').BoostInventoryItem[] }>('/api/boost/quests/claim', {
      method: 'POST',
      body: JSON.stringify({ quest_id }),
    });
  }

  // Telegram Stars Shop
  async getStarsProducts(): Promise<{
    success: boolean;
    shop_enabled: boolean;
    stars_mode: string;
    currency: string;
    products: import('@galaxy/shared').StarProduct[];
  }> {
    return this.request('/api/stars/products');
  }

  async getStarsEntitlements(): Promise<{
    success: boolean;
    entitlements: import('@galaxy/shared').PlayerEntitlement[];
    owned_products: string[];
    is_vip: boolean;
    vip_expires_at: number | null;
  }> {
    return this.request('/api/stars/entitlements');
  }

  async getStarsEquipped(): Promise<{
    success: boolean;
    equipped: {
      ship_skin?: string | null;
      combat_vfx?: string | null;
      base_theme?: string | null;
      galaxy_deco?: string | null;
    };
  }> {
    return this.request('/api/stars/equipped');
  }

  async createStarsInvoice(productId: string): Promise<{
    success: boolean;
    order_id: string;
    invoice_link: string;
    stars_amount: number;
    product_id: string;
    test_mode: boolean;
  }> {
    return this.request('/api/stars/invoice', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId }),
    });
  }

  async simulateTestPayment(orderId: string): Promise<{
    success: boolean;
    message: string;
    order: import('@galaxy/shared').StarOrder;
    entitlement: import('@galaxy/shared').PlayerEntitlement;
  }> {
    return this.request('/api/stars/simulate-test-payment', {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId }),
    });
  }

  async equipCosmetic(category: string, productId: string | null): Promise<{
    success: boolean;
    message: string;
    category: string;
    equipped: string | null;
  }> {
    return this.request('/api/stars/equip', {
      method: 'POST',
      body: JSON.stringify({ category, product_id: productId }),
    });
  }

  // --- REFERRAL REWARDS API ---
  async getReferralStatus(): Promise<ReferralStatusResponse> {
    return this.request('/api/referral/status');
  }

  async claimReferralMilestone(milestone: number): Promise<ReferralClaimResponse> {
    return this.request('/api/referral/claim', {
      method: 'POST',
      body: JSON.stringify({ milestone }),
    });
  }

  async completeTutorial(): Promise<{ success: boolean; tutorial_completed: boolean }> {
    return this.request('/api/referral/tutorial/complete', {
      method: 'POST',
    });
  }

  // --- SHOP 24H COSMETIC TRIALS API ---
  async getCosmeticTrialStatus(): Promise<{
    success: boolean;
    is_admin: boolean;
    max_trials: number;
    trials_used: number;
    trials_remaining: number;
    active_trials: Array<{
      id: string;
      product_id: string;
      category: string;
      trial_index: number;
      created_at: number;
      expires_at: number;
      is_active: boolean;
      remaining_hours: number;
    }>;
  }> {
    return this.request('/api/stars/trials');
  }

  async activateCosmeticTrial(productId: string): Promise<{
    success: boolean;
    trial: any;
    trials_remaining: number;
    message: string;
  }> {
    return this.request('/api/stars/trials/activate', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId }),
    });
  }

  // --- ADMIN SIMULATED TEST PURCHASE API ---
  async adminTestPurchase(productId: string): Promise<{
    success: boolean;
    order: any;
    entitlement: any;
    message: string;
  }> {
    return this.request('/api/stars/admin/test-purchase', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId }),
    });
  }

  async clearAdminTestPurchases(): Promise<{
    success: boolean;
    cleared_count: number;
    message: string;
  }> {
    return this.request('/api/stars/admin/clear-test-purchases', {
      method: 'POST',
    });
  }
}

export const api = new ApiClient();

